import React, { PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import MuiMenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import { i18n_or_as_low_as } from '@yi/i18n/phrases/i18n_or_as_low_as';
import { i18n_per_month } from '@yi/i18n/phrases/i18n_per_month';

import { navigate } from '~/common';
import { Button as DefaultButton, useLocale } from '~/components';
import {
  black50,
  cerise,
  ceriseHover,
  courseBannerZIndex,
  sizeMobile,
  sizeDesktop,
  sizeTablet,
  steelGrey,
  white50,
  turquoise,
} from '~/styles';

const PlayButtonWrapper = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid white;
  text-align: center;
  cursor: pointer;
  margin: auto;
`;

export const PlayButton: React.FC<
  PropsWithChildren<{
    onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  }>
> = ({ onClick }, ...props) => (
  <PlayButtonWrapper onClick={onClick} {...props}>
    <PlayArrow style={{ height: 65, width: 65, marginLeft: 2, marginTop: 24, fill: 'white' }} />
  </PlayButtonWrapper>
);

// height: 708px;
export const BlackOverlay = styled.div`
  width: 100%;
  background: ${black50};
  margin-top: -66px;
  padding-top: 4%;
  ${sizeMobile(css`
    padding-bottom: 50px;
  `)}
`;

export const BannerWrapper = styled.div<{ imgUrl?: string | null }>`
  top: 0;
  overflow: hidden;
  z-index: ${courseBannerZIndex};
  width: 100%;
  height: auto;
  background: ${black50};
  ${({ imgUrl }) =>
    imgUrl &&
    css`
      background-image: url('${imgUrl}');
      background-repeat: no-repeat;
      background-size: cover;
      background-position: right center;
    `}
`;

export const Video = styled.video`
  width: 100%;
  height: 100%;
  ~${sizeDesktop(css`
    width: auto;
    height: 100%;
  `)}

  position: relative;
  bottom: 50px;
`;

export const Select = styled(MuiSelect)`
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 5px;
  &.MuiFilledInput-root {
    border: 1.5px solid #fff;
    color: #fff;
  }
  &.MuiFilledInput-root:hover {
    background-color: ${white50};
  }
  &.MuiFilledInput-underline {
    &:before {
      border-bottom: none;
    }
    &:after {
      border-bottom: none;
    }
  }
  .MuiFilledInput-input {
    padding: 15px 35px 12px 10px;
    font-weight: bold;
    font-size: 14px;
  }
  svg.MuiSelect-icon {
    color: #fff;
  }
  min-width: 170px;
  height: 55px;
`;

export const MenuItem = styled(MuiMenuItem)`
  color: ${steelGrey};
  background-color: ${white50};
  &.MuiMenuItem-root {
    background-color: ;
  }

  visibilty: hidden;
  .MuiSvgIcon-root {
    display: none;
  }
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiMenuItem>>>;

export const TabHeading = styled.h2`
  text-transform: uppercase;
  color: ${turquoise};
  font-size: 30px;
  letter-spacing: -0.5px;
  text-align: center;
  margin-top: 50px;
`;
export const Link = styled.a`
  display: block;
  cursor: pointer;
  text-transform: uppercase;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  // padding: 2px 30px 0;
  border: 2px solid transparent;
  border-radius: 2px;
  color: #fff;
  background: #ef743c;
  width: 100%;

  margin: 0px 8px;
  padding: 0px 8px;
  height: 40px;
  min-width: 110px;
  ${sizeMobile(css`
    min-width: 95px;
  `)}
`;
export const PreviewButton = styled.button`
  cursor: pointer;
  text-transform: uppercase;
  font-size: 13px;
  line-height: 51px;
  font-weight: 500;
  text-align: center;
  padding: 2px 30px 0;
  border: 1px solid white;
  border-radius: 2px;
  color: #fff;
  background: transparent;
  width: 100%;
  margin: 0px 8px;
  padding: 0px 8px;
  height: 40px;
  max-width: 140px;
  min-width: 120px;
  ${sizeTablet(css`
    margin-bottom: 10px;
  `)}
`;
export const TabsWrapper = styled.div`
  ${sizeMobile(css`
    padding-left: 0;
    padding-right: 0;
  `)}
  padding-left: 106px;
  padding-right: 106px;
`;

interface ButtonProps {
  path: string;
  price: string;
}

const StyledButton = styled(DefaultButton)`
  height: 53px;
  margin: 0 15px 20px;
  align-items: center;
  border-radius: 4px;
  border: 2px solid ${cerise};
  background: none;
  text-transform: unset;
  color: #fff;
  padding: 2px 17px 0;
  ${sizeMobile(css`
    min-height: 60;
    max-width: 95%;
  `)}
  &:hover {
    background-color: ${ceriseHover};
  }
`;

export const SplitItButton: React.FC<PropsWithChildren<ButtonProps>> = ({ path, price }) => {
  const { locale } = useLocale();
  return (
    <StyledButton
      onClick={() => navigate(`${path}${path.includes('coupon') ? '&' : '/?'}currentTab=1`)}
    >
      {/* @TODO i18n use phrase variable */}
      {i18n_or_as_low_as(locale)} {price}
      {i18n_per_month(locale)}
      <img
        style={{ width: 85 }}
        src="https://images.ctfassets.net/p0sybd6jir6r/0Jvg6CjMuH78G0S4LPP4R/c97e9520bfc5102fb08bc83f6dba8d46/SI_Logo_White.png"
      />
    </StyledButton>
  );
};

export const ButtonRow = styled.div`
  display: inline-flex;
  margin-top: 20px;
  margin-bottom: 20px;
  width: 100%;
  justify-content: center;
  align-items: center;
  ${sizeTablet(css`
    display: block;
  `)}
`;

export const TextPrice = styled(Box)`
  font-size: 18px;
  font-weight: 500;
  color: white;
  margin-bottom: 5px;
  margin-top: 30px;
`;
